import { DropdownMenu as M, Popover as P } from 'radix-ui'
import type { ReactElement, ReactNode } from 'react'
import { Keys } from './Kbd.tsx'

export interface MenuItem {
  key: string
  label: ReactNode
  icon?: ReactNode
  keys?: string
  onSelect: () => void
  tone?: 'danger'
  disabled?: boolean
}

export type MenuEntry = MenuItem | { separator: true; key: string } | { heading: ReactNode; key: string }

export function Menu({ trigger, items, align = 'end', side = 'bottom' }: { trigger: ReactElement; items: readonly MenuEntry[]; align?: 'start' | 'end' | 'center'; side?: 'top' | 'bottom' | 'right' | 'left' }) {
  return (
    <M.Root modal={false}>
      <M.Trigger asChild>{trigger}</M.Trigger>
      <M.Portal>
        <M.Content className="rt-menu" align={align} side={side} sideOffset={6} collisionPadding={8}>
          {items.map((it) => {
            if ('separator' in it) return <M.Separator key={it.key} className="rt-menu__sep" />
            if ('heading' in it) return (
              <M.Label key={it.key} className="rt-menu__label rt-label">
                {it.heading}
              </M.Label>
            )
            return (
              <M.Item key={it.key} className="rt-menu__item" data-tone={it.tone} disabled={it.disabled} onSelect={it.onSelect}>
                {it.icon}
                <span>{it.label}</span>
                {it.keys ? <Keys keys={it.keys} className="rt-menu__hint" /> : null}
              </M.Item>
            )
          })}
        </M.Content>
      </M.Portal>
    </M.Root>
  )
}

export function Popover({ trigger, children, align = 'start', open, onOpenChange }: { trigger: ReactElement; children: ReactNode; align?: 'start' | 'end' | 'center'; open?: boolean; onOpenChange?: (o: boolean) => void }) {
  return (
    <P.Root open={open} onOpenChange={onOpenChange}>
      <P.Trigger asChild>{trigger}</P.Trigger>
      <P.Portal>
        <P.Content
          className="rt-popover"
          align={align}
          sideOffset={6}
          collisionPadding={8}
          // When one controlled popover closes because another opened (task cards on /robot), focus going back to
          // this trigger counts as a click outside the new one and closes it again. Only return focus when no other
          // popover is open.
          onCloseAutoFocus={(e) => {
            if (document.querySelector('.rt-popover[data-state="open"]')) e.preventDefault()
          }}
        >
          {children}
        </P.Content>
      </P.Portal>
    </P.Root>
  )
}
